/**
 * Query-key factory.
 *
 * Every key is built here so invalidation is reliable. Ad-hoc keys written at
 * the call site drift — `['startups', id]` in one file and `['startup', id]` in
 * another look equivalent and are not, so a mutation invalidates one and leaves
 * the other showing stale data.
 *
 * Keys are hierarchical: invalidating `startups.all` clears every startup
 * query, including individual details, because TanStack Query matches by
 * prefix.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.9
 */

/** Filters that identify a distinct list query. */
export type ListParams = Record<string, unknown>;

export const queryKeys = {
  startups: {
    all: ['startups'] as const,
    lists: () => [...queryKeys.startups.all, 'list'] as const,
    list: (params: ListParams) => [...queryKeys.startups.lists(), params] as const,
    details: () => [...queryKeys.startups.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.startups.details(), id] as const,
    /* Nested under the startup so invalidating the entity clears the scorecard
       too — saving one can change the band, which lists sort by. */
    scorecard: (id: string) => [...queryKeys.startups.detail(id), 'scorecard'] as const,
    /* Keyed by the whole check (name, website) so a changed field re-queries
       rather than reads a stale answer for a different pair. */
    duplicates: (params: ListParams) =>
      [...queryKeys.startups.all, 'duplicates', params] as const,
  },

  pilots: {
    all: ['pilots'] as const,
    lists: () => [...queryKeys.pilots.all, 'list'] as const,
    list: (params: ListParams) => [...queryKeys.pilots.lists(), params] as const,
    details: () => [...queryKeys.pilots.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.pilots.details(), id] as const,
    timeline: () => [...queryKeys.pilots.all, 'timeline'] as const,
  },

  customers: {
    all: ['customers'] as const,
    lists: () => [...queryKeys.customers.all, 'list'] as const,
    list: (params: ListParams) => [...queryKeys.customers.lists(), params] as const,
    details: () => [...queryKeys.customers.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.customers.details(), id] as const,
  },

  contacts: {
    all: ['contacts'] as const,
    lists: () => [...queryKeys.contacts.all, 'list'] as const,
    list: (params: ListParams) => [...queryKeys.contacts.lists(), params] as const,
    details: () => [...queryKeys.contacts.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.contacts.details(), id] as const,
  },

  legalFinance: {
    all: ['legal-finance'] as const,
    detail: (startupId: string) =>
      [...queryKeys.legalFinance.all, 'detail', startupId] as const,
  },

  attachments: {
    all: ['attachments'] as const,
    lists: () => [...queryKeys.attachments.all, 'list'] as const,
    list: (params: ListParams) => [...queryKeys.attachments.lists(), params] as const,
  },

  assets: {
    all: ['assets'] as const,
    lists: () => [...queryKeys.assets.all, 'list'] as const,
    list: (params: ListParams) => [...queryKeys.assets.lists(), params] as const,
    details: () => [...queryKeys.assets.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.assets.details(), id] as const,
    map: () => [...queryKeys.assets.all, 'map'] as const,
  },

  activities: {
    all: ['activities'] as const,
    lists: () => [...queryKeys.activities.all, 'list'] as const,
    list: (params: ListParams) => [...queryKeys.activities.lists(), params] as const,
  },

  auth: {
    all: ['auth'] as const,
    me: () => [...queryKeys.auth.all, 'me'] as const,
  },

  taxonomy: {
    all: ['taxonomy'] as const,
    byKind: (kind: string) => [...queryKeys.taxonomy.all, kind] as const,
  },

  search: {
    all: ['search'] as const,
    /* Keyed by the whole query object (term, limit, entity) so a narrowed
       "see all" view is cached separately from the mixed results. */
    query: (params: ListParams) => [...queryKeys.search.all, params] as const,
  },

  dashboard: {
    all: ['dashboard'] as const,
  },

  analytics: {
    all: ['analytics'] as const,
  },

  alerts: {
    all: ['alerts'] as const,
  },

  matchmaker: {
    all: ['matchmaker'] as const,
    forStartup: (params: ListParams) => [...queryKeys.matchmaker.all, params] as const,
  },

  admin: {
    all: ['admin'] as const,
    taxonomy: (params: ListParams) =>
      [...queryKeys.admin.all, 'taxonomy', params] as const,
    settings: () => [...queryKeys.admin.all, 'settings'] as const,
    users: () => [...queryKeys.admin.all, 'users'] as const,
  },
} as const;
